import { useState, memo } from "react";
import { Image, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getCountryFlagUrl } from "../../utils/countryFlag";
import { formatCityCountryLabel } from "../../utils/cityAsyncSelectOptions";
import { getEventObjectiveDisplayLabel } from "../../i18n/eventRegistrationI18n";
import {
  getAvatarUrl,
  getInitials,
  getRole,
  getMembershipBadgeKey,
  getConnectionMembershipDisplay,
  normalizeMembership,
} from "../../utils/connectionUserUtils";
import EventAttendeeCardActions from "./EventAttendeeCardActions";
import {
  AVATAR_MOBILE_STYLE,
  AVATAR_STYLE,
  COL,
  GAP,
  colStyle,
  type EventAttendeeCardProps,
} from "./eventAttendeeCardShared";

export type {
  ConnectionState,
  AttendeeWithState,
  EventAttendeeCardProps,
} from "./eventAttendeeCardShared";

function EventAttendeeCard({
  attendee,
  sequentialNumber,
  isCurrentUser,
  currentUserMembership,
  explorerLimitReached = false,
  isExplorerUser = false,
  onBlockedConnectionAction,
  onViewProfile,
  onSaveNote,
  onMessage,
  onConnect,
  onCancelRequest,
  onAccept,
  onIgnore,
  onRemoveConnection,
  sendConnectionId,
  cancellingId,
  hasNote = false,
  isSelected = false,
  onSelect,
}: EventAttendeeCardProps) {
  const { t } = useTranslation();
  const { user, actionUser, connectionState } = attendee;
  void onRemoveConnection;
  const [avatarError, setAvatarError] = useState(false);

  const avatarUrl = getAvatarUrl(user);
  const initials = getInitials(user);
  const role = getRole(user);
  const membershipTarget = {
    membershipName: user.membershipName || actionUser.membershipName,
    explorerAttendeeType:
      user.explorerAttendeeType ?? actionUser.explorerAttendeeType,
  };
  const membershipBadgeKey = getMembershipBadgeKey(membershipTarget);
  const membershipLabel = getConnectionMembershipDisplay(membershipTarget);
  const membershipNameFallback =
    membershipTarget.membershipName?.trim() || "";
  const membershipDisplay = membershipBadgeKey
    ? t(membershipBadgeKey)
    : membershipLabel ||
      normalizeMembership(membershipNameFallback) ||
      membershipNameFallback;
  const locationLabel = formatCityCountryLabel(user.city, user.country);
  const locationFlagUrl = getCountryFlagUrl(user.country, user.city);
  const showInitials = !avatarUrl || avatarError;
  const objectiveLabel = user.eventObjectives
    ? getEventObjectiveDisplayLabel(user.eventObjectives, t)
    : "";

  const actionProps = {
    user,
    actionUser,
    connectionState,
    isCurrentUser,
    currentUserMembership,
    explorerLimitReached,
    isExplorerUser,
    onBlockedConnectionAction,
    onViewProfile,
    onSaveNote,
    onMessage,
    onConnect,
    onCancelRequest,
    onAccept,
    onIgnore,
    sendConnectionId,
    cancellingId,
    hasNote,
  };

  const renderAvatar = (mobile: boolean) => {
    const sizeStyle = mobile ? AVATAR_MOBILE_STYLE : AVATAR_STYLE;
    const size = mobile ? 56 : COL.avatar;
    return (
      <div
        className="position-relative flex-shrink-0"
        style={{ width: size, height: size }}
      >
        {isCurrentUser && (
          <Badge
            bg="secondary"
            className="position-absolute top-0 start-0 translate-middle"
            style={{ fontSize: 9, zIndex: 1 }}
          >
            {t("connections.you")}
          </Badge>
        )}
        {showInitials ? (
          <div
            className="d-flex align-items-center justify-content-center text-white fw-bold rounded"
            style={{
              ...sizeStyle,
              background: "linear-gradient(180deg, #2366CD 0%, #3A97A4 100%)",
            }}
          >
            {initials}
          </div>
        ) : (
          <Image
            src={avatarUrl}
            alt={`${user.firstName} ${user.lastName}`}
            rounded
            loading="lazy"
            onError={() => setAvatarError(true)}
            style={sizeStyle}
          />
        )}
      </div>
    );
  };

  const locationBlock = locationLabel ? (
    <div className="d-inline-flex align-items-center gap-1 text-muted small">
      {locationFlagUrl && (
        <img
          src={locationFlagUrl}
          alt=""
          width={20}
          height={14}
          className="rounded flex-shrink-0"
        />
      )}
      <span>{locationLabel}</span>
    </div>
  ) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      className={`px-3 py-3 rounded-3 border shadow-sm ${
        isSelected ? "attendee-card-selected border-secondary" : "bg-white border-light"
      }`}
      style={{
        backgroundColor: isSelected ? "#eceff1" : "#fff",
        boxShadow: isSelected
          ? "0 0.125rem 0.5rem rgba(0, 0, 0, 0.1)"
          : "0 0.125rem 0.5rem rgba(0, 0, 0, 0.08)",
        cursor: "pointer",
        transition: "background-color 0.15s ease, box-shadow 0.15s ease",
      }}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.();
        }
      }}
    >
      {/* Desktop: flexible horizontal row — content shrinks, actions stay visible */}
      <div
        className="d-none d-lg-flex align-items-center flex-nowrap w-100 min-w-0"
        style={{ gap: GAP }}
      >
        <span
          className="text-muted small fw-semibold flex-shrink-0 text-end"
          style={{ width: COL.index }}
          aria-label={`Attendee ${sequentialNumber}`}
        >
          {sequentialNumber}.
        </span>

        {renderAvatar(false)}

        <div className="min-w-0" style={colStyle(COL.name, 1.1)}>
          <div className="d-flex align-items-center gap-2 min-w-0">
            <div className="fw-bold text-dark text-truncate">
              {`${user.firstName} ${user.lastName}`}
            </div>
          </div>
          <div className="text-muted small text-truncate" style={{ minHeight: 18 }}>
            {role || "\u00A0"}
          </div>
        </div>

        <div
          className="d-flex align-items-center gap-1 text-muted small min-w-0"
          style={colStyle(COL.city)}
        >
          {locationLabel ? (
            <>
              {locationFlagUrl && (
                <img
                  src={locationFlagUrl}
                  alt=""
                  width={20}
                  height={14}
                  className="rounded flex-shrink-0"
                />
              )}
              <span className="text-truncate">{locationLabel}</span>
            </>
          ) : (
            <span>&nbsp;</span>
          )}
        </div>

        <div
          className="d-flex align-items-center min-w-0"
          style={colStyle(COL.objective, 1.2)}
        >
          {objectiveLabel ? (
            <Badge
              bg="light"
              text="dark"
              pill
              className="fw-normal text-truncate mw-100"
              title={objectiveLabel}
            >
              {objectiveLabel}
            </Badge>
          ) : null}
        </div>

        <div
          className="d-flex align-items-center min-w-0"
          style={colStyle(COL.membership)}
        >
          {membershipDisplay ? (
            <Badge
              bg="light"
              text="dark"
              pill
              className="fw-bold text-uppercase text-truncate mw-100"
              style={{ fontSize: 10 }}
              title={membershipDisplay}
            >
              {membershipDisplay}
            </Badge>
          ) : null}
        </div>

        <EventAttendeeCardActions {...actionProps} mobile={false} />
      </div>

      {/* Mobile: profile card */}
      <div className="d-lg-none">
        <div className="d-flex align-items-start gap-3 mb-3">
          <span className="text-muted small fw-semibold pt-1">
            {sequentialNumber}.
          </span>
          {renderAvatar(true)}
          <div className="min-w-0 flex-grow-1">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="fw-bold text-dark">
                {`${user.firstName} ${user.lastName}`}
              </div>
            </div>
            {role ? <div className="text-muted small mb-1">{role}</div> : null}
            {locationBlock}
          </div>
        </div>

        {(objectiveLabel || membershipDisplay) && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            {objectiveLabel ? (
              <Badge bg="light" text="dark" pill className="fw-normal">
                {objectiveLabel}
              </Badge>
            ) : null}
            {membershipDisplay ? (
              <Badge
                bg="light"
                text="dark"
                pill
                className="fw-bold text-uppercase"
                style={{ fontSize: 10 }}
              >
                {membershipDisplay}
              </Badge>
            ) : null}
          </div>
        )}

        <EventAttendeeCardActions {...actionProps} mobile />
      </div>
    </div>
  );
}

export default memo(EventAttendeeCard);
